


import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
} from '@vue/composition-api';

import type { HokkaidoAirportCarExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';
import { dtFormat } from '@/lib/dateHelper';
import { PaneStyle } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  LIST_HARD_MAX_HEIGHT_PX,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import useHokkaidoAirportCars from '@/components/ExternalReports/HokkaidoAirport/composables/useCars';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelPc,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
    showCarIcons: {
      type: Boolean,
      default: true,
    },
    paneStyle: {
      type: Object as PropType<PaneStyle>,
      default: () => ({
        paneSideMinWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        paneSideMaxWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        listMinHeight: LIST_HARD_MAX_HEIGHT_PX,
        listMaxHeight: LIST_HARD_MAX_HEIGHT_PX,
      }),
    },
  },
  emits: ['click-car-on-car-list', 'change-show-car-icons', 'change-photo-type-visibility'],
  setup(props, { emit }) {
    const { carState } = useHokkaidoAirportCars();
    const checkedShowCarIcons = ref(props.showCarIcons);
    const photoTypes = computed<string[]>(() => {
      return [...props.userSettings.choices.photo_types];
    });
    const checkedPhotoTypes = ref<Record<string, boolean>>(photoTypes.value.reduce((acc, type) => {
      acc[type] = true;
      return acc;
    }, {} as Record<string, boolean>));
    const pcLabels = computed<HokkaidoAirportLabelPc>(() => (props.userSettings?.pc ?? {}) as HokkaidoAirportLabelPc);
    const visibleCars = computed<HokkaidoAirportCarExt[]>(() => carState.cars.filter(c => c.shouldShow));

    watch(() => props.showCarIcons, (value: boolean) => {
      checkedShowCarIcons.value = value;
    }, { immediate: true });

    const onClickCarOnCarList = (car: HokkaidoAirportCarExt) => {
      emit('click-car-on-car-list', car);
    };
    const onChangeShowCarIcons = () => {
      emit('change-show-car-icons');
    };

    const onChangePhotoTypeVisibility = (photoType: string) => {
      emit('change-photo-type-visibility', photoType, checkedPhotoTypes.value[photoType]);
    };
    return {
      // refs
      carState,
      photoTypes,
      checkedShowCarIcons,
      checkedPhotoTypes,
      pcLabels,
      visibleCars,
      // methods
      onClickCarOnCarList,
      onChangeShowCarIcons,
      onChangePhotoTypeVisibility,
      dtFormat,
    };
  },
});
